import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton, NeuSelect } from "../components/neu/Neu";
import { useCachedResource } from "../hooks/useCachedResource";
import { deleteDuty, listDuties, updateDuty } from "../api/duties";
import { listUsers } from "../api/directory";
import { DutyForm } from "../features/duty/DutyForm";
import { DutyList } from "../features/duty/DutyList";

const STATUSES = ["active", "done", "cancelled", "all"];

export default function DutiesPage() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [status, setStatus] = useState("active");
  const [creating, setCreating] = useState(false);

  const { data, reload: load } = useCachedResource(`duties:${status}`, () => listDuties({ status }), [status]);
  const duties = data || [];

  useEffect(() => {
    listUsers(null, "active").then(setEmployees);
  }, []);

  const remove = async (duty) => {
    await deleteDuty(duty.id);
    toast.success(t("duty.deleted"));
    load();
  };

  const setDutyStatus = async (duty, next) => {
    await updateDuty(duty.id, { status: next });
    toast.success(t("duty.updated"));
    load();
  };

  return (
    <AppShell
      title={t("duty.title")}
      subtitle={t("duty.subtitle")}
      actions={
        <NeuButton variant="primary" data-testid="duty-add-btn" className="flex items-center gap-2" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" /> {t("duty.add")}
        </NeuButton>
      }
    >
      <div className="grid gap-6">
        <div className="w-full sm:max-w-xs">
          <p className="label-xs mb-2">{t("duty.filter")}</p>
          <NeuSelect data-testid="duty-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s === "all" ? t("duty.all") : t(`duty.status.${s}`)}
              </option>
            ))}
          </NeuSelect>
        </div>

        {creating ? (
          <DutyForm
            employees={employees}
            onCancel={() => setCreating(false)}
            onSaved={() => {
              setCreating(false);
              load();
            }}
          />
        ) : null}

        <DutyList duties={duties} employees={employees} onDelete={remove} onStatus={setDutyStatus} />
      </div>
    </AppShell>
  );
}
